export const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8080"

interface PortalApiOptions extends RequestInit {
  skipJson?: boolean
}

export class PortalApiError extends Error {
  statusCode: number
  code?: string
  retryAfterSeconds?: number

  constructor(message: string, statusCode: number, code?: string, retryAfterSeconds?: number) {
    super(message)
    this.name = "PortalApiError"
    this.statusCode = statusCode
    this.code = code
    this.retryAfterSeconds = retryAfterSeconds
  }
}

function parseRetryAfterSeconds(res: Response) {
  const retryAfterHeader = res.headers.get("Retry-After")
  const resetHeader = res.headers.get("X-RateLimit-Reset")
  const rawValue = retryAfterHeader || resetHeader

  if (!rawValue) {
    return undefined
  }

  const seconds = Number.parseInt(rawValue, 10)
  return Number.isFinite(seconds) && seconds > 0 ? seconds : undefined
}

export async function portalApi<T>(path: string, options: PortalApiOptions = {}): Promise<T> {
  const { skipJson, ...fetchOptions } = options

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...fetchOptions.headers,
  }

  const res = await fetch(`${API_URL}${path}`, {
    ...fetchOptions,
    headers,
    credentials: "include",
  })

  if (!res.ok) {
    let errorData: { error?: string; code?: string } = {}
    try {
      errorData = await res.json()
    } catch {
      // ignore parse errors
    }
    throw new PortalApiError(
      errorData.error || `HTTP ${res.status}`,
      res.status,
      errorData.code,
      parseRetryAfterSeconds(res)
    )
  }

  if (res.status === 204 || skipJson) {
    return undefined as T
  }

  return res.json()
}

export async function portalApiBlob(path: string, options: PortalApiOptions = {}): Promise<Blob> {
  const headers: HeadersInit = {
    ...options.headers,
  }

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
    credentials: "include",
  })

  if (!res.ok) {
    let errorData: { error?: string; code?: string } = {}
    try {
      errorData = await res.json()
    } catch {
      // ignore parse errors
    }
    throw new PortalApiError(
      errorData.error || `HTTP ${res.status}`,
      res.status,
      errorData.code,
      parseRetryAfterSeconds(res)
    )
  }

  return res.blob()
}
